import { GitBranch } from 'lucide-react';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';

interface TemplateSummaryLike {
  title?: string;
  name?: string;
  version?: number | null;
  avgQualityScore?: string | null;
  source?: string | null;
  sourceRepoUrl?: string | null;
  sourceFilePath?: string | null;
}

interface StepTemplateMetadataProps {
  step: WorkflowBuilderStep;
  template?: TemplateSummaryLike | null;
  compact?: boolean;
}

export function StepTemplateMetadata({ step, template, compact = false }: StepTemplateMetadataProps) {
  if (!step.templateSlug && !step.templateId) return null;
  const title =
    step.templateName ?? template?.title ?? template?.name ?? step.templateSlug ?? `Template ${step.templateId}`;
  const version = step.templateVersion ?? template?.version ?? null;
  const qualityScore = formatQuality(step.templateQualityScore ?? template?.avgQualityScore ?? null);
  const provenance = step.templateProvenance ?? {
    source: template?.source ?? null,
    sourceRepoUrl: template?.sourceRepoUrl ?? null,
    sourceFilePath: template?.sourceFilePath ?? null,
  };
  const sourceLabel = [provenance.source, provenance.sourceFilePath].filter(Boolean).join(' / ') || 'org';

  return (
    <div
      className={
        compact
          ? 'mt-1 flex flex-wrap items-center gap-1 text-[10px]'
          : 'mt-2 rounded-sm border bg-surface-alt/60 p-2 text-xs'
      }
    >
      {!compact && <div className="font-medium text-heading">{title}</div>}
      <span className="inline-flex items-center gap-1 rounded-sm bg-surface px-1.5 py-0.5 text-muted">
        <GitBranch size={10} /> v{version ?? 'n/a'}
      </span>
      {qualityScore && <span className="rounded-sm bg-surface px-1.5 py-0.5 text-muted">Q {qualityScore}</span>}
      <span className="rounded-sm bg-surface px-1.5 py-0.5 text-muted">{sourceLabel}</span>
    </div>
  );
}

function formatQuality(value: string | null) {
  if (!value) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${Math.round(numeric * 100)}%`;
}
